import React from "react";

import { useSelector } from "react-redux";
import { Box, Flex, Image } from "rebass";

//------------------------------------------------------------------------------
import {
    FooterWrapper,
    MobileImage,
    OverlapImage,
    PageConstraint,
    StyledBackground,
    StyledFooter,
    StyledHeading
} from "./styled";
import { PORTAL_IMAGE } from "constants/imagePath";
import { AnchorLink, MediaQuery, Paragraph } from "@sisuwellness/web-components";
import ExtraBreakpointWrapper from "components/ExtraBreakpointWrapper";
import { useTranslation } from "react-i18next";
//------------------------------------------------------------------------------

const Footer = () => {
    const { t } = useTranslation();

    function renderNonMobileVersion(imgName) {
        return (
            <Flex flexDirection="row" width="100%">
                <StyledBackground width="100%">
                    <MediaQuery devices={["desktop"]}>
                        <Box pt="90px" width="65%" mx="auto">
                            <img width="100%" src={`${PORTAL_IMAGE}/footer/${imgName}.svg`} alt={"mobile"} />
                        </Box>
                    </MediaQuery>
                    <Flex
                        height="100%"
                        justifyContent="space-between"
                        pt="50px"
                        pl="20px"
                        width={["100%", "100%", "35%"]}
                        flexDirection="column"
                    >
                        <Flex width="100%" flexDirection="column">
                            <Paragraph size={["36px", "36px", "28px"]} fontWeight="300" pb="32px">
                                {t(
                                    "members_portal_web.components.footer.still_looking",
                                    "Still looking for something?"
                                )}
                            </Paragraph>
                            <Flex
                                flexWrap="wrap"
                                flexDirection={["column", "row", "row"]}
                                justifyContent="space-between"
                            >
                                <Box width={["100%", "50%", "50%"]}>
                                    <StyledHeading pb="24px" renderAs="h6">
                                        {t("words.about", "About")}
                                    </StyledHeading>
                                    <AnchorLink
                                        href="https://www.sisuhealth.co.uk/about-sisu-health"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Paragraph pb="24px" size="20px" fontWeight="300">
                                            {t("words.about_sisu_health", "About SiSU Health")}
                                        </Paragraph>
                                    </AnchorLink>
                                    <AnchorLink
                                        href="https://sisuhealth.co.uk/privacy-policy-uk/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Paragraph size="20px" pb="24px" fontWeight="300">
                                            {t("members_portal_web.components.footer.privacy_policy", "Privacy Policy")}{" "}
                                            (UK)
                                        </Paragraph>
                                    </AnchorLink>
                                    <AnchorLink
                                        href="https://sisuhealth.co.uk/privacy-policy-eu/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Paragraph size="20px" pb="24px" fontWeight="300">
                                            {t("members_portal_web.components.footer.privacy_policy", "Privacy Policy")}{" "}
                                            (EU)
                                        </Paragraph>
                                    </AnchorLink>
                                    <AnchorLink
                                        href="https://sisuhealth.co.uk/terms-use-uk/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Paragraph size="20px" pb="24px" fontWeight="300">
                                            {t("members_portal_web.components.footer.terms_of_use", "Terms of Use")}{" "}
                                            (UK)
                                        </Paragraph>
                                    </AnchorLink>
                                    <AnchorLink
                                        href="https://sisuhealth.co.uk/terms-use-eu/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Paragraph size="20px" pb="24px" fontWeight="300">
                                            {t("members_portal_web.components.footer.terms_of_use", "Terms of Use")}{" "}
                                            (EU)
                                        </Paragraph>
                                    </AnchorLink>
                                    <AnchorLink
                                        href="https://www.sisuhealth.co.uk/sisu-health-policies/cookie-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Paragraph size="20px" pb="24px" fontWeight="300">
                                            {t("members_portal_web.components.footer.cookie_policy", "Cookie Policy")}
                                        </Paragraph>
                                    </AnchorLink>
                                </Box>
                                <Box width={["100%", "50%", "45%"]}>
                                    <StyledHeading pb="24px" renderAs="h6">
                                        {t("words.contact", "Contact")}
                                    </StyledHeading>
                                    <AnchorLink
                                        href="https://www.sisuhealth.co.uk/contact-sisu-health"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Paragraph pb={["60px", "40px", "24px"]} size="20px" fontWeight="300">
                                            {t("members_portal_web.components.footer.contact_sisu", "Contact SiSU")}
                                        </Paragraph>
                                    </AnchorLink>
                                </Box>
                            </Flex>
                        </Flex>
                        <MediaQuery>
                            <Image
                                width="130px"
                                src={`${PORTAL_IMAGE}/authenticatedHeader/Logo.svg`}
                                alt="logo"
                                mb={["60px", "0"]}
                            />
                            <Paragraph
                                size="20px"
                                fontWeight="300"
                                pt={["24px", "24px", "0px"]}
                                pb={["60px", "50px", "24px"]}
                            >
                                © {new Date().getFullYear()} —
                                {t("members_portal_web.components.footer.sisu_health_group", "SiSU Health")}
                            </Paragraph>
                        </MediaQuery>
                    </Flex>
                </StyledBackground>
            </Flex>
        );
    }

    const initialValue = useSelector(state => state.auth.citizen);

    const defaultCountryCode = initialValue.userProfile && initialValue.userProfile.countryCode;

    const imgName = defaultCountryCode === "AU" || defaultCountryCode === "GB" ? defaultCountryCode : "other";

    return (
        <ExtraBreakpointWrapper screen="macbook" mt="130px">
            <FooterWrapper bg="hpNavBackground" id="member-portal-footer" mt={["160px", "200px"]}>
                <PageConstraint mx="auto">
                    <StyledFooter>
                        <MediaQuery devices={["desktop"]}>
                            <OverlapImage src={`${PORTAL_IMAGE}/footer/girlAndDog.svg`} alt={"mobile"} />
                        </MediaQuery>
                        <MediaQuery devices={["mobile", "tablet"]}>
                            <MobileImage src={`${PORTAL_IMAGE}/footer/${imgName}-m.svg`} alt={"mobile"} />
                        </MediaQuery>
                        <StyledBackground data-testid="footer" data-printid="footer">
                            {renderNonMobileVersion(imgName)}
                        </StyledBackground>
                    </StyledFooter>
                </PageConstraint>
            </FooterWrapper>
        </ExtraBreakpointWrapper>
    );
};

export default Footer;
